/**
 * Procedural mountain skybox: GLSL port of ProceduralSkyboxBake.shader baked once per (theme, nightMode) into a
 * 2048x1024 equirect (Unity bakes 4096x2048, SkyboxBaker.cs:12-13; halved to bound mobile memory), then drawn as a
 * full-screen background sampled by view direction (ProceduralSkybox.shader). The camera never rotates, so the
 * backdrop is fixed on screen while the molecule turns.
 */
import {
  ClampToEdgeWrapping, LinearFilter, LinearMipmapLinearFilter, Matrix4, Mesh, OrthographicCamera, RepeatWrapping, RGBAFormat,
  Scene, SRGBColorSpace, UnsignedByteType, Vector4, WebGLRenderTarget, type WebGLRenderer, type Texture,
} from 'three';
import { SKYBOX_DEFAULT_KEY, SKYBOX_NIGHT_MODE_DEFAULT, SKYBOX_THEMES, type SkyboxPalette } from '@nanome-lite/look';
import { fullscreenGeometry, makeMaterial } from './gl';
import { LIGHT_DIR_VIEW } from './gl';
import { COMPOSITE_FRAG, FULLSCREEN_VERT, SKYBOX_BAKE_FRAG, SKYBOX_DRAW_FRAG } from './shaders';

export const SKYBOX_BAKE_WIDTH = 2048;
export const SKYBOX_BAKE_HEIGHT = 1024;

/** Property block defaults of ProceduralSkyboxBake.shader:13-22 (the baker only overrides colours and _SunDir). */
export const SKYBOX_BAKE_DEFAULTS = {
  fog: 0.3, ridges: 7, spikiness: 0.8, roughness: 0.5, height: 0.5, frequency: 1.5, depthSpread: 0.5, sunWarmth: 0.5, seed: 420,
} as const;

/** Inverse of the bake tonemap pow(x/(x+0.6)*1.55, 0.95) (SkyboxBaker.cs:72-82). */
export function inverseTonemap(v: number): number {
  const t = Math.pow(Math.min(1, Math.max(0, v)), 1 / 0.95);
  return (0.6 * t) / Math.max(1.55 - t, 0.001);
}

/**
 * Sun direction passed to the bake = -light.forward (EnvironmentManager.cs:70) in Unity world space. LIGHT_DIR_VIEW
 * is the GL view-space direction toward the light, i.e. Unity (x, y, -z) of it; the bake's own mapping is Unity's.
 */
export const SKYBOX_SUN_DIR: [number, number, number] = [LIGHT_DIR_VIEW.x, LIGHT_DIR_VIEW.y, -LIGHT_DIR_VIEW.z];

export function resolvePalette(theme: string, nightMode: boolean): SkyboxPalette {
  const t = SKYBOX_THEMES.find((x) => x.key === theme) ?? SKYBOX_THEMES.find((x) => x.key === SKYBOX_DEFAULT_KEY)!;
  return nightMode ? t.dark : t.light;
}

export class Skybox {
  theme = SKYBOX_DEFAULT_KEY;
  nightMode: boolean = SKYBOX_NIGHT_MODE_DEFAULT;
  private readonly cache = new Map<string, WebGLRenderTarget>();
  private readonly bakeMaterial = makeMaterial(FULLSCREEN_VERT, SKYBOX_BAKE_FRAG, null, {
    depthTest: false, depthWrite: false, blend: false,
    uniforms: {
      uZenithColor: { value: new Vector4() }, uMidColor: { value: new Vector4() }, uHorizonColor: { value: new Vector4() },
      uSunColor: { value: new Vector4() }, uGroundNearColor: { value: new Vector4() }, uGroundFarColor: { value: new Vector4() },
      uFog: { value: SKYBOX_BAKE_DEFAULTS.fog }, uRidges: { value: SKYBOX_BAKE_DEFAULTS.ridges }, uSpikiness: { value: SKYBOX_BAKE_DEFAULTS.spikiness },
      uRoughness: { value: SKYBOX_BAKE_DEFAULTS.roughness }, uHeight: { value: SKYBOX_BAKE_DEFAULTS.height }, uFrequency: { value: SKYBOX_BAKE_DEFAULTS.frequency },
      uDepthSpread: { value: SKYBOX_BAKE_DEFAULTS.depthSpread }, uSunDir: { value: new Vector4(SKYBOX_SUN_DIR[0], SKYBOX_SUN_DIR[1], SKYBOX_SUN_DIR[2], 0) },
      uSunWarmth: { value: SKYBOX_BAKE_DEFAULTS.sunWarmth }, uSeed: { value: SKYBOX_BAKE_DEFAULTS.seed },
    },
  });
  readonly drawMaterial = makeMaterial(FULLSCREEN_VERT, SKYBOX_DRAW_FRAG, null, {
    depthTest: false, depthWrite: false, blend: false,
    uniforms: { uSky: { value: null as Texture | null }, uInvProjection: { value: new Matrix4() } },
  });
  readonly mesh: Mesh;
  private readonly bakeScene = new Scene();
  private readonly bakeCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);

  constructor() {
    this.mesh = new Mesh(fullscreenGeometry(), this.drawMaterial);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -1000;
    this.bakeScene.add(new Mesh(fullscreenGeometry(), this.bakeMaterial));
  }

  /** Returns the baked equirect for the current theme, baking on first use. */
  texture(renderer: WebGLRenderer): Texture {
    const key = `${this.theme}|${this.nightMode ? 'dark' : 'light'}`;
    let rt = this.cache.get(key);
    if (!rt) {
      rt = new WebGLRenderTarget(SKYBOX_BAKE_WIDTH, SKYBOX_BAKE_HEIGHT, {
        format: RGBAFormat, type: UnsignedByteType, depthBuffer: false, stencilBuffer: false, colorSpace: SRGBColorSpace,
        generateMipmaps: true, minFilter: LinearMipmapLinearFilter, magFilter: LinearFilter, wrapS: RepeatWrapping, wrapT: ClampToEdgeWrapping,
      });
      rt.texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
      const p = resolvePalette(this.theme, this.nightMode);
      const u = this.bakeMaterial.uniforms;
      const set = (name: string, c: [number, number, number]) => (u[name]!.value as Vector4).set(inverseTonemap(c[0]), inverseTonemap(c[1]), inverseTonemap(c[2]), 1);
      set('uZenithColor', p.zenith); set('uMidColor', p.mid); set('uHorizonColor', p.horizon);
      set('uSunColor', p.sun); set('uGroundNearColor', p.groundNear); set('uGroundFarColor', p.groundFar);
      const prev = renderer.getRenderTarget();
      renderer.setRenderTarget(rt);
      renderer.render(this.bakeScene, this.bakeCamera);
      renderer.setRenderTarget(prev);
      this.cache.set(key, rt);
    }
    return rt.texture;
  }

  /** Point the draw material at the current bake and projection. */
  prepare(renderer: WebGLRenderer, projection: Matrix4): void {
    this.drawMaterial.uniforms.uSky!.value = this.texture(renderer);
    (this.drawMaterial.uniforms.uInvProjection!.value as Matrix4).copy(projection).invert();
  }

  /** Drop every bake (context loss). */
  invalidate(): void { for (const rt of this.cache.values()) rt.dispose(); this.cache.clear(); }

  dispose(): void { this.invalidate(); this.bakeMaterial.dispose(); this.drawMaterial.dispose(); }
}

/** Fullscreen resolve of the linear scene target to an sRGB output. */
export function createComposite(): { mesh: Mesh; setTexture(t: Texture | null): void; dispose(): void } {
  const material = makeMaterial(FULLSCREEN_VERT, COMPOSITE_FRAG, null, { depthTest: false, depthWrite: false, blend: false, uniforms: { uScene: { value: null as Texture | null } } });
  const mesh = new Mesh(fullscreenGeometry(), material);
  mesh.frustumCulled = false;
  return { mesh, setTexture: (t) => { material.uniforms.uScene!.value = t; }, dispose: () => material.dispose() };
}
